import React, { useState, useEffect } from 'react';
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import {
    initializeEditorEdit,
    updateIntegrationStore,
    clearEditor,
    getIntegration,
    validateIntegrationNameUnique
} from "../../../actions/integrationEditorActions";
import ArgoToolbarHook from "../../common/ArgoToolbarHook";
import ArgoTextFieldHook from "../../common/ArgoTextFieldHook";
import ArgoTextFieldSelectHook from "../../common/ArgoTextFieldSelectHook";
import ArgoIconButtonToggleViewFieldTextHook from "../../common/ArgoIconButtonToggleViewFieldTextHook";
import { IntegrationTypes } from "../../../constants/integrationConstants";
import * as validate from "../../../utilities/validate";


function IntegrationEditorHook(props) {
    const [isLoading, setIsLoading] = useState(false);
    const [nameErrorText, setNameErrorText] = useState(" ");
    const [typeErrorText, setTypeErrorText] = useState("");
    const [toolbarErrorText, setToolbarErrorText] = useState("");
    const [urlErrorText, setUrlErrorText] = useState("");
    const [apiKey1ErrorText, setApiKey1ErrorText] = useState("");
    const [apiKey2ErrorText, setApiKey2ErrorText] = useState("");
    const [info3ErrorText, setInfo3ErrorText] = useState("");
    const [info4ErrorText, setInfo4ErrorText] = useState("");
    const [info5ErrorText, setInfo5ErrorText] = useState("");
    const [viewApiKey1, setViewApiKey1] = useState(false);
    const [viewApiKey2, setViewApiKey2] = useState(false);

    const dispatch = useDispatch();
    const integration = useSelector(state => state.integrationEditor.integration);
    const options = useSelector(state => state.integrationEditor.options);

    useEffect(() => {
        if (props.id) {
            setIsLoading(true);
            dispatch(getIntegration(props.id));
        }
        
        checkForErrorsOnPage();
        
        return () => {
            dispatch(clearEditor());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.id, dispatch]);

    useEffect(() => {
        if (isLoading && integration) {
            setIsLoading(false);
            dispatch(initializeEditorEdit(integration));
        }
        
        checkForErrorsOnPage();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [integration, isLoading, dispatch]);

    const handleToolbarButtonClick = (event, fieldName, buttonTitle) => {
        switch (buttonTitle) {
            case "SAVE":
                props.save(integration);
                break;
            case "CANCEL":
                props.cancel();
                break;
            // no default
        }
    };

    const onTextChange = (event, fieldName) => {
        let value = event.target.value;
        let updatedIntegration = { ...integration }; // copy integration template

        // Because of issues with the ApiKeys being treated as passwords, I modified the way change is stored to keep prev entries and not the ****
        if ((fieldName === "apiKey1") || (fieldName === "apiKey2")) {
            let newPayload = "";
            if (value !== "") {
                for (let i = 0; i < value.length; i++) {
                    if (value.substring(i, i + 1) === "*") {
                        newPayload = newPayload + updatedIntegration[fieldName].substring(i, i + 1);
                    }
                    else {
                        newPayload = newPayload + value.substring(i, i + 1);
                        updatedIntegration[fieldName] = updatedIntegration[fieldName].substring(0, i) + " " + updatedIntegration[fieldName].substring(i);
                    }
                }
            }

            updatedIntegration[fieldName] = newPayload.replace(/\s/g, '');
        }
        else {
            updatedIntegration[fieldName] = value;
        }
        
        dispatch(updateIntegrationStore(updatedIntegration));

        if (fieldName === "name") {
            dispatch(validateIntegrationNameUnique(updatedIntegration.name));
        }
    };

    const checkForErrorsOnPage = () => {
        if (!integration) return;
        
        let isOAUTH2 = integration.integrationType === "OAUTH2";
        let newNameErrorText = validate.templateNameUnique({template: integration, options: options}, "Integration Name");
        let newTypeErrorText = integration.integrationType.length === 0 ? "Type is required" : " ";
        let newUrlErrorText = integration.integrationUrl.length === 0 ? "URL is required." : " ";
        let newApiKey1ErrorText = integration.apiKey1.length === 0 ? isOAUTH2 ? "Client ID is required." : "API Key 1 is required." : " ";
        let newApiKey2ErrorText = integration.apiKey2.length === 0 ? isOAUTH2 ? "Secret is required." : "API Key 2 is required." : " ";
        let newInfo3ErrorText = isOAUTH2 ? integration.info3.length === 0 ? "Token Endpoint is required." : " " : " ";
        let newInfo4ErrorText = isOAUTH2 ? integration.info4.length === 0 ? "Grant Type is required." : " " : " ";
        let newInfo5ErrorText = isOAUTH2 ? integration.info5.length === 0 ? "Scope is required." : " " : " ";
        let newToolbarErrorText = "";

        if (newNameErrorText !== " ") {
            newToolbarErrorText = newNameErrorText;
        }

        if (newTypeErrorText !== " ") {
            newToolbarErrorText = newToolbarErrorText === "" ? newTypeErrorText : newToolbarErrorText + ",  " + newTypeErrorText;
        }

        if (newUrlErrorText !== " ") {
            newToolbarErrorText = newToolbarErrorText === "" ? newUrlErrorText : newToolbarErrorText + ",  " + newUrlErrorText;
        }

        if (newApiKey1ErrorText !== " ") {
            newToolbarErrorText = newToolbarErrorText === "" ? newApiKey1ErrorText : newToolbarErrorText + ",  " + newApiKey1ErrorText;
        }

        if (newApiKey2ErrorText !== " ") {
            newToolbarErrorText = newToolbarErrorText === "" ? newApiKey2ErrorText : newToolbarErrorText + ",  " + newApiKey2ErrorText;
        }

        if (newInfo3ErrorText !== " ") {
            newToolbarErrorText = newToolbarErrorText === "" ? newInfo3ErrorText : newToolbarErrorText + ",  " + newInfo3ErrorText;
        }

        if (newInfo4ErrorText !== " ") {
            newToolbarErrorText = newToolbarErrorText === "" ? newInfo4ErrorText : newToolbarErrorText + ",  " + newInfo4ErrorText;
        }

        if (newInfo5ErrorText !== " ") {
            newToolbarErrorText = newToolbarErrorText === "" ? newInfo5ErrorText : newToolbarErrorText + ",  " + newInfo5ErrorText;
        }

        setNameErrorText(newNameErrorText);
        setTypeErrorText(newTypeErrorText);
        setUrlErrorText(newUrlErrorText);
        setApiKey1ErrorText(newApiKey1ErrorText);
        setApiKey2ErrorText(newApiKey2ErrorText);
        setInfo3ErrorText(newInfo3ErrorText);
        setInfo4ErrorText(newInfo4ErrorText);
        setInfo5ErrorText(newInfo5ErrorText);
        setToolbarErrorText(newToolbarErrorText);
    };

    const toggleViewApiKey = (fieldName, value) => {
        if (fieldName === "viewApiKey1") {
            setViewApiKey1(value);
        } else if (fieldName === "viewApiKey2") {
            setViewApiKey2(value);
        }
    };

    // For future use when Test Connection Button is ready
    // const testConnection = (id, DTO) => {
    //     // Test Connection
    // };

    if (isLoading || !integration) {
        // TODO place a loading screen here
        return null;
    }

    return (
        <div style={{ height: "100%" }}>
            <ArgoToolbarHook
                fileName="IntegrationEditor"
                fieldName="save-or-cancel"
                toolbarErrorText={toolbarErrorText}
                isValidating={options.isValidating}
                onClick={handleToolbarButtonClick}
                width={props.width}
            />

            <div id="IntegrationEditor-height-div" style={{ height: "50px" }}>&nbsp;</div>

            <div style={{ height: "100%", marginTop: "30px" }}>
                <div style={{ justifyContent: "space-between" }}>
                    <div style={{ flexGrow: "2", paddingLeft: "32px", paddingRight: "32px" }}>

                        <ArgoTextFieldHook
                            fileName="IntegrationEditor"
                            fieldName="name"
                            label="Name"
                            value={integration.name}
                            multiline
                            onChange={onTextChange}
                            errorText={nameErrorText}
                            autoFocus={true}
                        />

                        <ArgoTextFieldSelectHook
                            fileName="IntegrationEditor"
                            fieldName="integrationType"
                            label="Type"
                            value={integration.integrationType}
                            menuItems={IntegrationTypes}
                            onChange={onTextChange}
                            errorText={typeErrorText}
                        />

                        <ArgoTextFieldHook
                            fileName="IntegrationEditor"
                            fieldName="integrationUrl"
                            label="URL"
                            value={integration.integrationUrl}
                            multiline
                            onChange={onTextChange}
                            errorText={urlErrorText}
                        />

                        <div style={{ width: "100%", display: "flex" }}>
                            <ArgoTextFieldHook
                                fileName="IntegrationEditor"
                                fieldName="apiKey1"
                                label={integration.integrationType === "OAUTH2" ? "Client ID" : "API Key 1"}
                                value={viewApiKey1 ? integration.apiKey1 : "*".repeat(integration.apiKey1.length)}
                                multiline
                                onChange={onTextChange}
                                errorText={apiKey1ErrorText}
                            />

                            <ArgoIconButtonToggleViewFieldTextHook
                                fileName="IntegrationEditor"
                                fieldName="viewApiKey1"
                                onClick={toggleViewApiKey}
                            />
                        </div>

                        <div style={{ width: "100%", display: "flex" }}>
                            <ArgoTextFieldHook
                                fileName="IntegrationEditor"
                                fieldName="apiKey2"
                                label={integration.integrationType === "OAUTH2" ? "Secret" : "API Key 2"}
                                value={viewApiKey2 ? integration.apiKey2 : "*".repeat(integration.apiKey2.length)}
                                multiline
                                onChange={onTextChange}
                                errorText={apiKey2ErrorText}
                            />

                            <ArgoIconButtonToggleViewFieldTextHook
                                fileName="IntegrationEditor"
                                fieldName="viewApiKey2"
                                onClick={toggleViewApiKey}
                            />
                        </div>

                        {integration.integrationType === "OAUTH2" ?
                            <div>
                                <ArgoTextFieldHook
                                    fileName="IntegrationEditor"
                                    fieldName="info3"
                                    label="Token Endpoint"
                                    value={integration.info3}
                                    multiline
                                    onChange={onTextChange}
                                    errorText={info3ErrorText}
                                />

                                <ArgoTextFieldHook
                                    fileName="IntegrationEditor"
                                    fieldName="info4"
                                    label="Grant Type"
                                    value={integration.info4}
                                    multiline
                                    onChange={onTextChange}
                                    errorText={info4ErrorText}
                                />

                                <ArgoTextFieldHook
                                    fileName="IntegrationEditor"
                                    fieldName="info5"
                                    label="Scope"
                                    value={integration.info5}
                                    multiline
                                    onChange={onTextChange}
                                    errorText={info5ErrorText}
                                />
                            </div> : ""}

                        {/*<Button id="IntegrationEditor-test-connection" variant="contained" primary style={IntegrationEditorStyle.button}*/}
                        {/*onClick={testConnection.bind(this, "", "")} tabIndex={1}>TEST CONNECTION</Button>*/}
                    </div>
                </div>
            </div>
        </div>
    );
}

IntegrationEditorHook.propTypes = {
    id: PropTypes.number,
    integration: PropTypes.object,
    options: PropTypes.object,
    save: PropTypes.func,
    cancel: PropTypes.func,
    width: PropTypes.string
};

export default IntegrationEditorHook;